import React, { useState, useEffect } from "react";
import {
  handleRegister,
  handleRemoveRegistration,
} from "services/registrationService";
import { fetchUsers } from "services/userService";
import UserSearch from "./UserSearch";
import RegistrationConfirmationForm from "./RegistrationConfirmationForm";
import { UserPlus, UserMinus, Ban } from "lucide-react";

const TournamentRegistrationForm = ({
  tournament,
  user,
  registrations,
  prefetchedUsers = [],
  isAdmin = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersFetched, setUsersFetched] = useState(false);

  const registrationCount = registrations?.length || 0;
  const registrationLimit = tournament.registrationLimit || tournament.RegistrationLimit || 100;
  const isFull = registrationCount >= registrationLimit;

  // Tournament is passed if the date is before today (not including today)
  const isTournamentPassed = tournament.date && (() => {
    const tournamentDate = new Date(tournament.date);
    const today = new Date();
    tournamentDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return tournamentDate < today;
  })();

  const isRegistrationDisabled = isFull || tournament.registrationClosed || isTournamentPassed;

  const userRegistration = registrations?.find(
    (reg) => reg.userId === user?.uid || reg.teamMateId === user?.uid
  );

  // 1. Set users from prefetchedUsers if present
  useEffect(() => {
    if (isAdmin && prefetchedUsers.length > 0) {
      setUsers(prefetchedUsers);
      setUsersFetched(true);
    }
  }, [prefetchedUsers, isAdmin, user?.uid]);

  // 2. Fetch users on demand if needed
  const fetchUsersIfNeeded = async () => {
    if (!usersFetched) {
      const all = await fetchUsers();
      const filtered = all.filter((u) => u.id !== user?.uid);
      setUsers(filtered);
      setUsersFetched(true);
    }
  };

  const handleRegisterClick = () => {
    if (isRegistrationDisabled) return;
    setIsEditing(true);
    if (tournament.type === "Double Open" && !usersFetched) {
      fetchUsersIfNeeded();
    }
  };

  const handleUnregister = () => {
    if (!userRegistration) return;

    const confirmed = window.confirm("Voulez-vous vraiment vous désinscrire ?");
    if (!confirmed) return;

    handleRemoveRegistration(userRegistration.id);
  };

  const handleConfirm = async (finalName, teamMateId, partnerName) => {
    await handleRegister(
      tournament,
      registrations,
      user,
      finalName,
      teamMateId,
      partnerName
    );
    setIsEditing(false);
  };

  const handleUnregisterClick = () => {
    if ((tournament.registrationClosed || isTournamentPassed) && !isAdmin) {
      alert(
        "Les inscriptions sont fermées. Veuillez contacter l'organisateur pour vous désinscrire."
      );
      return;
    }
    handleUnregister();
  };

  return (
    <div className="w-full mt-4">
      {user && !isEditing && (
        <>
          {userRegistration ? (
            <button
              onClick={handleUnregisterClick}
              disabled={isTournamentPassed}
              className={`flex items-center justify-center gap-2 px-4 py-2 text-white font-semibold text-lg rounded-lg transition w-full md:w-auto ${
                isTournamentPassed
                  ? "bg-slate-700 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-500"
              }`}
            >
              <UserMinus size={20} /> Se désinscrire
            </button>
          ) : (
            <button
              onClick={handleRegisterClick}
              disabled={tournament.registrationClosed || isRegistrationDisabled}
              className={`flex items-center justify-center gap-2 px-4 py-2 text-white font-semibold text-lg rounded-lg transition w-full md:w-auto ${
                tournament.registrationClosed || isRegistrationDisabled
                  ? "bg-slate-700 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-500"
              }`}
            >
              {tournament.registrationClosed ? (
                <><Ban size={20} /> Inscriptions fermées</>
              ) : isTournamentPassed ? (
                <><Ban size={20} /> Tournoi terminé</>
              ) : isFull ? (
                <><Ban size={20} /> Complet</>
              ) : (
                <><UserPlus size={20} /> S'inscrire</>
              )}
            </button>
          )}
        </>
      )}

      {isEditing && (
        <div className="mt-4">
          <RegistrationConfirmationForm
            tournament={tournament}
            user={user}
            users={users}
            onConfirm={handleConfirm}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      )}

      {isAdmin && (
        <div className="mt-2">
          <UserSearch
            tournament={tournament}
            registrations={registrations}
            users={prefetchedUsers}
          />
        </div>
      )}
    </div>
  );
};

export default TournamentRegistrationForm;
